import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Card,
  Button,
  message,
  Spin,
  Input,
  Select,
  Popconfirm,
} from "antd";
import { useEffect, useState } from "react";
import {
  ReloadOutlined,
  FilterOutlined,
  UserSwitchOutlined,
  EditOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import MotionHoc from "../../../utils/MotionHoc";
import {
  HEADERS_EXPORT_BEN,
  POST_MAIN_CHANGE_JOBS,
  POST_UPDATE_CHANGE_JOBS,
} from "../../API/apiUrls";
import useLawyerNamesNew from "../../../hook/UseLawyerNameNew";
import axios from "axios";

const Main = () => {
  const { nameLawyers, loadingUseLawyerNames } = useLawyerNamesNew({
    auto: true,
    toast: false,
  });
  const userId = parseInt(localStorage.getItem("USER_ID"));

  const [arrayTable, setArrayTable] = useState([]);
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  // ✅ track แยกต่อแถว: { [loanId]: newLawyerValue }
  const [editingMap, setEditingMap] = useState({}); // loanId → true/false
  const [changeMap, setChangeMap] = useState({}); // loanId → userId ใหม่

  // ── Lawyer options ────────────────────────────────────────
  const lawyerOptions = (nameLawyers ?? [])
    .filter(
      (l) => l.ACTIVE_STATUS === 1 && l.COMPANY_ID !== 3 && l.ROLE_ID === 3,
    )
    .map((l) => ({ label: l.NNAME ?? l.NAME1, value: l.id ?? l.userId }));

  // ── Load ──────────────────────────────────────────────────
  const loadMainData = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        POST_MAIN_CHANGE_JOBS,
        { userId: 1 },
        { headers: HEADERS_EXPORT_BEN },
      );
      if (res.status === 200) {
        if (res.data.success === "ไม่พบข้อมูล") {
          message.warning("ไม่พบข้อมูล");
          return;
        }
        const newData = (res.data?.data ?? []).map((item, i) => ({
          ...item,
          key: i + 1,
        }));
        setArrayTable(newData);
        setDataArr(newData);
        setEditingMap({});
        setChangeMap({});
      } else {
        message.error("ไม่มีข้อมูล");
        setArrayTable([]);
        setDataArr([]);
      }
    } catch (err) {
      console.error(err);
      message.error(`ไม่พบข้อมูล: ${err.message}`);
      setArrayTable([]);
      setDataArr([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMainData();
  }, []);

  // ── Filter ────────────────────────────────────────────────
  const handleFilterData = () => {
    setLoading(true);
    const q = searchInputTable.trim().toLowerCase();
    const result = (dataArr ?? []).filter(
      (item) =>
        !q ||
        [item.CUSCOD, item.CONTNO, item.NAME1, item.NAME2]
          .map((v) => String(v ?? "").toLowerCase())
          .some((val) => val.includes(q)),
    );
    setArrayTable(result);
    setTimeout(() => setLoading(false), 250);
    result.length === 0
      ? message.error("ไม่พบข้อมูล")
      : message.success(`พบข้อมูล ${result.length} รายการ`);
  };

  // ── Post ──────────────────────────────────────────────────
  const postStatus = async (record) => {
    const newLawyer = changeMap[record.loanId];
    if (!newLawyer) {
      message.warning("กรุณาเลือกทนายใหม่ก่อน");
      return;
    }

    const payload = {
      userId,
      CONTNO: record.CONTNO,
      nameCustomer:
        `${record.SNAM ?? ""}${record.NAME1 ?? ""} ${record.NAME2 ?? ""}`.trim(),
      loanId: record.loanId,
      lawsuitId: record.lawsuitId,
      currentStatus: record.currentStatus,
      lawyerOld: record.userId,
      lawyerNew: newLawyer,
    };
    try {
      const res = await axios.post(POST_UPDATE_CHANGE_JOBS, payload, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res.status === 200) {
        message.success("เปลี่ยนทนายสำเร็จ");
        loadMainData();
      }
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        err.message;
      const errorMap = {
        400: `ข้อมูลไม่ถูกต้อง: ${serverMsg}`,
        404: "ไม่พบปลายทาง (404)",
        500: "เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)",
      };
      message.error(
        errorMap[status] ??
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
      );
    }
  };

  // ── Columns ───────────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      align: "center",
      width: "10%",
      render: (_, __, idx) => idx + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      align: "center",
      width: "15%",
    },
    {
      title: "ชื่อ-นามสกุล",
      align: "left",
      width: "20%",
      render: (_, r) =>
        `${r.SNAM ?? ""}${r.NAME1 ?? ""} ${r.NAME2 ?? ""}`.trim(),
    },
    {
      title: "สถานะ",
      align: "center",
      width: "15%",
      render: (_, r) => (
        <Tag color="blue">{r.mainStatusDescription ?? "-"}</Tag>
      ),
    },
    {
      title: "ผู้รับผิดชอบ",
      align: "center",
      width: "20%",
      render: (_, r) => {
        const isEditing = !!editingMap[r.loanId];
        return (
          <Select
            value={changeMap[r.loanId] ?? r.userId}
            style={{ width: 150 }}
            loading={loadingUseLawyerNames}
            disabled={!isEditing} // ✅ disable ถ้าแถวนี้ไม่ได้แก้ไข
            options={lawyerOptions}
            onChange={(val) =>
              setChangeMap((prev) => ({ ...prev, [r.loanId]: val }))
            }
          />
        );
      },
    },
    {
      title: "การจัดการ",
      align: "center",
      width: "20%",
      render: (_, r) => {
        const isEditing = !!editingMap[r.loanId];
        return isEditing ? (
          <Space>
            {/* ✅ ยืนยันก่อน submit */}
            <Popconfirm
              title="ยืนยันการเปลี่ยนทนาย"
              description={`เปลี่ยนจาก ${r.NNAME ?? r.userId} → ทนายใหม่`}
              onConfirm={() => postStatus(r)}
              okText="ยืนยัน"
              cancelText="ยกเลิก"
            >
              <Button type="primary" size="small" icon={<UserSwitchOutlined />}>
                เปลี่ยนทนาย
              </Button>
            </Popconfirm>
            {/* ✅ ยกเลิก reset เฉพาะแถวนี้ */}
            <Button
              danger
              size="small"
              icon={<CloseOutlined />}
              onClick={() => {
                setEditingMap((prev) => ({ ...prev, [r.loanId]: false }));
                setChangeMap((prev) => {
                  const n = { ...prev };
                  delete n[r.loanId];
                  return n;
                });
              }}
            />
          </Space>
        ) : (
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() =>
              setEditingMap((prev) => ({ ...prev, [r.loanId]: true }))
            }
          >
            แก้ไข
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <Card style={{ marginBottom: 12 }}>
        <Space>
          <Input
            placeholder="ค้นหาสัญญา / ชื่อ / เลขบัตร"
            value={searchInputTable}
            onChange={(e) => setSearchInputTable(e.target.value)}
            onPressEnter={handleFilterData}
            style={{ width: 260, borderRadius: 8 }}
            allowClear
          />
          <Button
            icon={<FilterOutlined />}
            onClick={handleFilterData}
            disabled={dataArr.length === 0}
            style={{
              borderRadius: 8,
              backgroundColor: "#fff7d6",
              border: "1px solid #ffe58f",
            }}
          >
            กรองข้อมูล
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setSearchInputTable("");
              setArrayTable(dataArr);
            }}
            disabled={dataArr.length === 0}
          >
            ล้าง
          </Button>
          <Button
            icon={<ReloadOutlined />}
            type="primary"
            ghost
            onClick={loadMainData}
            loading={loading}
          >
            โหลดใหม่
          </Button>
        </Space>
      </Card>

      <Card>
        <Spin spinning={loading} size="large" tip="Loading...">
          <Table
            size="small"
            columns={columns}
            dataSource={arrayTable}
            rowKey={(r) => r.loanId ?? r.key}
            scroll={{ x: "max-content" }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
            }}
            footer={() => (
              <span>จำนวนสัญญาทั้งหมด {arrayTable.length} รายการ</span>
            )}
          />
        </Spin>
      </Card>
    </>
  );
};

const MainChangeLawyerJobs = MotionHoc(Main);
export default MainChangeLawyerJobs;
